// Aging cellar: environment drift, daily aging, rind washing, taking wheels out.
import { CELLAR } from '../config/constants';
import { agingMultiplier, CHEESES, type AgeRule } from '../data/cheeses';
import type { AgingStack, InventoryCheese, SaveState } from './GameState';
import { rackCapacity } from '../data/coinUpgrades';

export function tempOk(t: number): boolean {
  return t >= CELLAR.TEMP_LOW && t <= CELLAR.TEMP_HIGH;
}

export function humOk(h: number): boolean {
  return h >= CELLAR.HUM_LOW && h <= CELLAR.HUM_HIGH;
}

/** Aging days gained per calendar day under the current dials. */
export function envSpeed(cellar: SaveState['cellar']): number {
  const t = tempOk(cellar.temp);
  const h = humOk(cellar.humidity);
  if (t && h) return CELLAR.SPEED_OPTIMAL;
  if (t || h) return CELLAR.SPEED_HALF;
  return CELLAR.SPEED_POOR;
}

export function envLabel(cellar: SaveState['cellar']): string {
  const speed = envSpeed(cellar);
  if (speed === CELLAR.SPEED_OPTIMAL) return 'optimal';
  if (speed === CELLAR.SPEED_HALF) return 'tolerable';
  return 'poor';
}

export function rackCount(s: SaveState): number {
  return s.cellar.racks.reduce((n, r) => n + r.qty, 0);
}

export function rackSpace(s: SaveState): number {
  return rackCapacity(s) - rackCount(s);
}

export function rackWheels(s: SaveState, cheeseId: string, qty: number, stars: number, ownerId = 'player', ownerName = 'Player'): AgingStack {
  const stack: AgingStack = {
    id: `rack_${s.cellar.nextId++}`,
    cheeseId,
    qty,
    stars,
    ageDays: 0,
    rackedDay: s.day,
    lastWashAge: 0,
    missedWashes: 0,
    stalledDays: 0,
    ownerId,
    ownerName,
  };
  s.cellar.racks.push(stack);
  return stack;
}

export function ageRule(cheeseId: string): AgeRule | null {
  const def = CHEESES[cheeseId];
  return def && def.aging.ageable ? def.aging : null;
}

/** Value multiplier for a stack right now, washes included. */
export function stackValueMult(stack: AgingStack, s?: SaveState): number {
  const rule = ageRule(stack.cheeseId);
  if (!rule) return 1;
  const penalty = CELLAR.MISSED_WASH_VALUE_PENALTY * (1 - 0.02 * (s ? (s.coinUpgradeLevels?.rind_stewardship ?? 0) : 0));
  return Math.max(0.5, agingMultiplier(rule, stack.ageDays) - penalty * stack.missedWashes);
}

export function stackNeedsWash(stack: AgingStack): boolean {
  const rule = ageRule(stack.cheeseId);
  if (!rule || !rule.washedRind) return false;
  return stack.ageDays - stack.lastWashAge >= CELLAR.WASH_EVERY_DAYS;
}

export function stacksNeedingWash(s: SaveState): AgingStack[] {
  return s.cellar.racks.filter(stackNeedsWash);
}

/** Wash every rind that is due. Returns the number of stacks washed. */
export function washDue(s: SaveState): number {
  let n = 0;
  for (const stack of s.cellar.racks) {
    if (stackNeedsWash(stack)) {
      stack.lastWashAge = stack.ageDays;
      n++;
    }
  }
  return n;
}

export interface CellarDayReport {
  speed: number;
  spoiled: { cheeseId: string; qty: number }[];
  missedWashes: number;
  ready: { cheeseId: string; qty: number }[];
}

/** Day-end processing: the room drifts, the wheels age, neglected rinds are noted. */
export function processCellarDay(s: SaveState, rng: () => number = Math.random): CellarDayReport {
  const c = s.cellar;
  const level = s.coinUpgradeLevels?.cellar_insulation ?? 0;
  c.temp = Math.round((c.temp + (rng() * 2 - 1) * CELLAR.DRIFT_TEMP * (1 - 0.02 * level)) * 10) / 10;
  c.humidity = Math.round(Math.min(100, Math.max(40, c.humidity + (rng() * 2 - 1) * CELLAR.DRIFT_HUM * (1 - 0.02 * level))) * 10) / 10;
  const speed = envSpeed(c);
  const report: CellarDayReport = { speed, spoiled: [], missedWashes: 0, ready: [] };
  const kept: AgingStack[] = [];
  for (const stack of c.racks) {
    const rule = ageRule(stack.cheeseId);
    if (!rule) continue;
    const before = stack.ageDays;
    if (speed > 0) {
      stack.ageDays = Math.round((stack.ageDays + speed) * 100) / 100;
      stack.stalledDays = 0;
    } else {
      stack.stalledDays++;
    }
    if (rule.washedRind && stack.ageDays - stack.lastWashAge > CELLAR.WASH_EVERY_DAYS + CELLAR.WASH_GRACE_DAYS) {
      stack.missedWashes++;
      stack.lastWashAge = stack.ageDays;
      report.missedWashes++;
    }
    if (stack.stalledDays >= CELLAR.STALL_SPOIL_DAYS || stack.ageDays >= rule.spoilAtDays) {
      report.spoiled.push({ cheeseId: stack.cheeseId, qty: stack.qty });
      continue;
    }
    if (before < rule.minAgeDays && stack.ageDays >= rule.minAgeDays) report.ready.push({ cheeseId: stack.cheeseId, qty: stack.qty });
    kept.push(stack);
  }
  c.racks = kept;
  return report;
}

/** Take wheels off the rack into inventory with their aging value baked in. */
export function takeOut(s: SaveState, stackId: string, qty: number): InventoryCheese | null {
  const i = s.cellar.racks.findIndex((r) => r.id === stackId);
  if (i < 0) return null;
  const stack = s.cellar.racks[i];
  const n = Math.min(qty, stack.qty);
  if (n <= 0) return null;
  const item: InventoryCheese = {
    kind: 'cheese',
    cheeseId: stack.cheeseId,
    qty: n,
    stars: stack.stars,
    madeDay: s.day,
    agedMult: Math.round(stackValueMult(stack, s) * 100) / 100,
    ownerId: stack.ownerId ?? 'player',
    ownerName: stack.ownerName ?? 'Player',
  };
  stack.qty -= n;
  if (stack.qty <= 0) s.cellar.racks.splice(i, 1);
  s.inventory.push(item);
  return item;
}
